import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Edit, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';

// Move EditableField outside to prevent recreation
const EditableField = ({
  label,
  field,
  value,
  type = 'text',
  isEditing,
  editValue,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onChange,
  isUpdating
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {isEditing ? (
        <div className="flex items-center space-x-2">
          <input
            ref={inputRef}
            type={type}
            name={field}
            value={editValue}
            onChange={onChange}
            className="input flex-1"
            placeholder={`Enter new ${field}`}
          />
          <button
            onClick={() => onSaveEdit(field)}
            disabled={isUpdating}
            className={`p-2 rounded-md transition-colors ${isUpdating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
              }`}
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            onClick={onCancelEdit}
            disabled={isUpdating}
            className="p-2 bg-gray-300 text-gray-600 rounded-md hover:bg-gray-400 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <input
            type={type}
            value={value}
            disabled
            className="input flex-1 bg-gray-50 border border-gray-200 rounded-md text-gray-600"
          />
          <button
            onClick={() => onStartEdit(field)}
            className="ml-2 p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const PasswordRequirement = ({ met, text }) => (
  <div className={`flex items-center text-sm ${met ? 'text-green-600' : 'text-gray-500'}`}>
    {met ? (
      <CheckCircle className="h-4 w-4 mr-2" />
    ) : (
      <AlertCircle className="h-4 w-4 mr-2" />
    )}
    {text}
  </div>
);

export default function Profile() {
  const { user, checkAuthStatus } = useAuth();
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    createdAt: ''
  });
  const [editingField, setEditingField] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/profile', {
        credentials: 'include',
        headers: { 'x-api-token': localStorage.getItem('api_token') || '' }
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData({
          username: data.user.username,
          email: data.user.email,
          createdAt: new Date(data.user.created_at).toLocaleDateString()
        });
        setEditForm({
          username: data.user.username,
          email: data.user.email
        });
      } else {
        toast.error('Failed to load profile data');
      }
    } catch (error) {
      toast.error('Network error while loading profile');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (field) => {
    setEditingField(field);
    setEditForm({
      username: profileData.username,
      email: profileData.email
    });
    setProfileError('');
    setProfileSuccess(false);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditForm({
      username: profileData.username,
      email: profileData.email
    });
    setProfileError('');
    setProfileSuccess(false);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
    setProfileError('');
    setProfileSuccess(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (username) => {
    return username.length >= 3 && username.length <= 50 && /^[a-zA-Z0-9_-]+$/.test(username);
  };

  const handleProfileUpdate = async (field) => {
    const value = editForm[field];

    // Validation
    if (field === 'email' && !validateEmail(value)) {
      setProfileError('Please enter a valid email address');
      return;
    }

    if (field === 'username' && !validateUsername(value)) {
      setProfileError('Username must be 3-50 characters and contain only letters, numbers, underscores, and hyphens');
      return;
    }

    setIsUpdatingProfile(true);
    setProfileError('');
    setProfileSuccess(false);

    try {
      const response = await fetch('/api/profile/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-token': localStorage.getItem('api_token') || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          field: field,
          value: value
        }),
      });

      const data = await response.json();

      if (data.success) {
        setProfileSuccess(true);
        setProfileData(prev => ({
          ...prev,
          [field]: value
        }));
        setEditingField(null);
        toast.success(`${field === 'username' ? 'Username' : 'Email'} updated successfully!`);
        // Refresh auth context to update user data
        await checkAuthStatus();
      } else {
        setProfileError(data.message || `Failed to update ${field}`);
      }
    } catch (error) {
      setProfileError('Network error. Please try again.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordChange = (e) => {
    // Update password in state but don't expose it in HTML
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      errors: {
        length: password.length < minLength,
        uppercase: !hasUpperCase,
        lowercase: !hasLowerCase,
        numbers: !hasNumbers,
        special: !hasSpecialChar
      }
    };
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    const validation = validatePassword(passwordForm.newPassword);
    if (!validation.isValid) {
      setPasswordError('Password does not meet requirements');
      return;
    }

    setIsPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess(false);

    try {
      const response = await fetch('/api/profile/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-token': localStorage.getItem('api_token') || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPasswordSuccess(true);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        toast.success('Password changed successfully!');
      } else {
        setPasswordError(data.message || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Network error. Please try again.');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 w-full px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-96 bg-gray-200 dark:bg-gray-800 rounded"></div>
            <div className="h-96 bg-gray-200 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full px-8 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Profile Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          Manage your account information and security settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Account Information</h3>
            </div>
          </div>
          <div className="card-body space-y-4">
            <EditableField
              key="username-field"
              label="Username"
              field="username"
              value={profileData.username}
              isEditing={editingField === 'username'}
              editValue={editForm.username}
              onStartEdit={startEditing}
              onCancelEdit={cancelEditing}
              onSaveEdit={handleProfileUpdate}
              onChange={handleEditChange}
              isUpdating={isUpdatingProfile}
            />
            <EditableField
              key="email-field"
              label="Email Address"
              field="email"
              value={profileData.email}
              type="email"
              isEditing={editingField === 'email'}
              editValue={editForm.email}
              onStartEdit={startEditing}
              onCancelEdit={cancelEditing}
              onSaveEdit={handleProfileUpdate}
              onChange={handleEditChange}
              isUpdating={isUpdatingProfile}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
              <input
                type="text"
                value={profileData.createdAt}
                disabled
                className="input w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-600"
              />
            </div>

            {/* Profile Update Messages */}
            {profileError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {profileError}
              </div>
            )}
            {profileSuccess && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded-md">
                Profile updated successfully!
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <Lock className="h-5 w-5 mr-2 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Change Password</h3>
            </div>
          </div>
          <div className="card-body">
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    onChange={handlePasswordChange}
                    required
                    className="input w-full pr-10"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    onChange={handlePasswordChange}
                    required
                    className="input w-full pr-10"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    onChange={handlePasswordChange}
                    required
                    className="input w-full pr-10"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {passwordForm.newPassword && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                  <div className="space-y-1">
                    <PasswordRequirement
                      met={passwordForm.newPassword.length >= 8}
                      text="At least 8 characters"
                    />
                    <PasswordRequirement
                      met={/[A-Z]/.test(passwordForm.newPassword)}
                      text="One uppercase letter"
                    />
                    <PasswordRequirement
                      met={/[a-z]/.test(passwordForm.newPassword)}
                      text="One lowercase letter"
                    />
                    <PasswordRequirement
                      met={/\d/.test(passwordForm.newPassword)}
                      text="One number"
                    />
                    <PasswordRequirement
                      met={/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword)}
                      text="One special character"
                    />
                  </div>
                </div>
              )}

              {/* Error/Success Messages */}
              {passwordError && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="text-green-600 text-sm bg-green-50 p-3 rounded-md">
                  Password changed successfully!
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isPasswordLoading}
                className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${isPasswordLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                {isPasswordLoading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 